import React from 'react';
import { formatCurrency } from 'utils';
import { useFormContext } from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';
import { CurrencyInputField, SelectField, DateField, InputField } from 'components/form';
import { CreditLineFormValues } from '../model';
import { generateCreditLineLog } from '../lib';
import { creditStatusValues } from '../constants';
import { DateFormats } from 'constants/common';
import { CreditLineRequest, UpdateCreditLineRequestInput, CreditLineStatusEnum } from '@trnsact/trnsact-shared-types';
import { CreditLineNotes } from './CreditLineNotes';
import { FetchResult } from '@apollo/client';

interface Props {
  userName: string;
  isSaving: boolean;
  requestedAmount: number;
  status: CreditLineStatusEnum;
  onCreditLineUpdate: (data: UpdateCreditLineRequestInput) => Promise<FetchResult<any> | undefined>;
  creditLineRequest?: CreditLineRequest;
}

export const CreditLineForm = ({
  status,
  userName,
  isSaving,
  requestedAmount,
  onCreditLineUpdate,
  creditLineRequest,
}: Props) => {
  const {
    reset,
    control,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = useFormContext<CreditLineFormValues>();

  // Only the fields written by the Save button (and the pending status-change log preview)
  // should enable it.
  const isDirty = !!(
    dirtyFields.notes ||
    dirtyFields.status ||
    dirtyFields.allNotes ||
    dirtyFields.customerNumber ||
    dirtyFields.approvedAmount ||
    dirtyFields.expirationDate
  );

  // --- Line Requested / Requested Line (amount) ---

  const handleChangeAmount = (nextAmountValue: number) => {
    setValue('approvedAmount', nextAmountValue, { shouldDirty: true });
  };

  // --- Status ---

  const handleChangeStatus = (nextStatusValue: CreditLineStatusEnum) => {
    setValue('status', nextStatusValue, { shouldDirty: true });

    const logMessage = generateCreditLineLog({
      userName,
      currentStatus: status,
      nextStatus: nextStatusValue,
      prevNotes: getValues('allNotes'),
    });

    setValue('allNotes', logMessage, { shouldDirty: true });
  };

  // --- Save ---

  const handleSave = async () => {
    const values = getValues();
    const nextAllNotes = values.notes ? `${values.allNotes}\n\n${values.notes}` : values.allNotes;

    await onCreditLineUpdate({
      creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
      approvedAmount: values.approvedAmount,
      status: values.status,
      customerNumber: values.customerNumber.trim() || null,
      expirationDate: values.expirationDate ? values.expirationDate.toISOString() : null,
      notes: nextAllNotes,
    });

    reset({ ...values, allNotes: nextAllNotes, notes: '' });
  };

  // --- Layout ---

  return (
    <Box className="lineSection">
      <Typography component="span" variant="subtitle2">
        Line Requested {formatCurrency(requestedAmount, true)}
      </Typography>

      <CurrencyInputField
        name="approvedAmount"
        control={control}
        label="Approved Line"
        customOnChange={handleChangeAmount}
        textFieldProps={{ fullWidth: true }}
      />

      <SelectField
        label="Status"
        control={control}
        name="status"
        options={creditStatusValues}
        extraAction={nextValue => handleChangeStatus(nextValue as CreditLineStatusEnum)}
      />

      <InputField name="customerNumber" control={control} label="Customer Number" inputProps={{ fullWidth: true }} />

      <DateField
        control={control}
        label="Expiration Date"
        format={DateFormats.Short}
        name="expirationDate"
        slotProps={{ textField: { fullWidth: true } }}
      />

      <CreditLineNotes isLoading={isSaving} />

      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" disabled={!isDirty || isSaving} onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
